import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore, type Language } from '../stores/app-store';
import { FlagIcon } from './flag-icon';
import { getBundledSystemLanguages, useLanguages } from '../hooks/use-languages';

interface LanguageSwitcherProps {
  /**
   * When true, renders all language options as a flat inline list instead of a
   * dropdown popup. Use this inside overflow-clipped containers (e.g. the mobile
   * slide-out menu) so the absolutely-positioned popup is not cut off.
   */
  inline?: boolean;
  /**
   * When true, the trigger button shows only the flag on small screens (no
   * chevron, no border box). On md+ it shows flag + chevron as normal.
   * The dropdown always shows flag + full language name regardless.
   */
  compact?: boolean;
}

export function LanguageSwitcher({ inline = false, compact = false }: LanguageSwitcherProps) {
  const { t, i18n } = useTranslation();
  const { language, setLanguage } = useAppStore();
  const { data: languages = getBundledSystemLanguages() } = useLanguages();
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Determine if dropdown should open upward based on available space
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const dropdownHeight = 400;
      const spaceBelow = window.innerHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      setOpenUpward(spaceBelow < dropdownHeight && spaceAbove > spaceBelow);
    }
  }, [isOpen]);

  const currentLanguage = languages.find((l) => l.code === language) || languages[0];

  const handleLanguageChange = (newLanguage: Language) => {
    setLanguage(newLanguage);
    i18n.changeLanguage(newLanguage);
    setIsOpen(false);
  };

  // ── Inline mode: flat list, no dropdown popup ──────────────────────────────
  if (inline) {
    return (
      <div className="space-y-1">
        {languages.map((lang) => (
          <button
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className={`flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left transition-colors ${
              language === lang.code
                ? 'bg-primary/10 text-primary'
                : 'text-foreground hover:bg-accent'
            }`}
          >
            <FlagIcon language={lang.code} className="w-8 h-6 flex-shrink-0" />
            <div className="flex-1">
              <div className="text-sm font-medium">{lang.nativeName}</div>
              <div className="text-xs text-muted-foreground">{lang.englishName}</div>
            </div>
            {language === lang.code && (
              <svg className="h-4 w-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            )}
          </button>
        ))}
      </div>
    );
  }

  // ── Default / compact dropdown mode ───────────────────────────────────────
  return (
    <div ref={dropdownRef} className="relative">
      {/* Trigger button:
          - compact + mobile  → flag only, no border, no chevron (clean icon tap-target)
          - compact + desktop → flag + chevron with border
          - not compact       → flag + chevron with border always
      */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t('languageSwitcher.currentLanguage', 'Current language: {{name}}', { name: currentLanguage?.nativeName })}
        aria-expanded={isOpen}
        className={
          compact
            ? // Mobile: bare flag tap-target; md+: standard bordered button
              'flex items-center rounded-lg p-1 transition-colors hover:bg-accent md:gap-1.5 md:border md:border-input md:bg-background md:px-2.5 md:py-2'
            : // Always bordered
              'flex items-center gap-1.5 rounded-lg border border-input bg-background px-2.5 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground'
        }
      >
        {/* Flag — always shown */}
        <FlagIcon language={currentLanguage?.code ?? 'en'} className="w-7 h-5 flex-shrink-0" />

        {/* Chevron — hidden on mobile when compact */}
        <svg
          className={`h-3.5 w-3.5 opacity-60 transition-transform ${isOpen ? 'rotate-180' : ''} ${compact ? 'hidden md:block' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown panel — always shows flag + full language name */}
      {isOpen && (
        <div
          className={`absolute right-0 z-[200] w-56 rounded-lg border border-border bg-card shadow-xl max-h-96 overflow-y-auto ${
            openUpward ? 'bottom-full mb-2' : 'top-full mt-2'
          }`}
        >
          <div className="p-2">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={`flex w-full items-center gap-3 rounded-md p-3 text-left transition-colors ${
                  language === lang.code ? 'bg-primary/10 text-primary' : 'hover:bg-accent'
                }`}
              >
                <FlagIcon language={lang.code} className="w-8 h-6 flex-shrink-0" />
                <div className="flex-1">
                  <div className="text-sm font-medium">{lang.nativeName}</div>
                  <div className="text-xs text-muted-foreground">{lang.englishName}</div>
                </div>
                {language === lang.code && (
                  <svg className="h-4 w-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
